import { useSearchParams } from 'react-router-dom';
import { NvsLogo } from '../components/NvsLogo';

/**
 * Sign-in confirmation interstitial.
 *
 * Magic-link / invite emails point here instead of straight at
 * /api/auth/callback. The one-time token is only exchanged when a human
 * clicks the button below (a real navigation to the Worker callback).
 *
 * Why: corporate email security (Microsoft Defender / SafeLinks, Mimecast,
 * Proofpoint) pre-fetches links in incoming mail to scan them. A GET straight
 * to /api/auth/callback would let the scanner consume the single-use token
 * before the recipient ever clicks, so the real click fails with "link
 * invalid or expired". Scanners follow links but don't click buttons, so
 * gating the exchange behind an explicit click keeps the token intact.
 */
export function AuthConfirm() {
  const [params] = useSearchParams();
  const tokenHash = params.get('token_hash');
  const type = params.get('type') ?? 'magiclink';
  const next = params.get('next') ?? '/dashboard';

  return (
    <main className="nvs-login-main nvs-confirm">
      <div className="nvs-login-card">
        <div className="nvs-brand-lockup nvs-confirm-brand">
          <NvsLogo className="nvs-brand-logo" />
          <div className="nvs-wordmark">
            <span className="nvs-wordmark-name">NVS</span>
            <span className="nvs-wordmark-sub">Logistics</span>
          </div>
        </div>

        {tokenHash ? (
          <>
            <div className="nvs-welcome">
              <h2>Confirm sign-in</h2>
              <p className="muted">
                Click the button below to securely sign in to the reporting portal.
                No password needed.
              </p>
            </div>
            {/* A real form POST so the browser performs a top-level navigation on
                submit. POST (unlike a GET link) is never cached by Cloudflare's
                CDN, so the request always reaches the Worker callback — which
                exchanges the token, sets the session cookie, and 302-redirects
                to `next`. Email scanners follow links but don't submit forms, so
                the one-time token stays intact until the user clicks. */}
            <form method="POST" action="/api/auth/callback" className="nvs-confirm-form">
              <input type="hidden" name="token_hash" value={tokenHash} />
              <input type="hidden" name="type" value={type} />
              <input type="hidden" name="next" value={next} />
              <button type="submit" className="nvs-primary-btn">
                <svg className="nvs-btn-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Sign in to Portal
              </button>
            </form>
            <p className="nvs-terms muted">This link can only be used once and expires shortly.</p>
          </>
        ) : (
          <>
            <div className="nvs-welcome">
              <h2>Link incomplete</h2>
              <p className="muted">
                This sign-in link is missing its token. Request a fresh link from the
                login page.
              </p>
            </div>
            <a className="nvs-primary-btn" href="/login">
              Back to sign in
            </a>
          </>
        )}
      </div>
    </main>
  );
}
